import { useEffect, useState } from 'react'
import { WIDGET_KEY } from '../widget.constants'
import { showToastError } from 'hooks/useToast'
import { WidgetComponentProps } from '../../wilsonChat.types'
import { useMe } from 'hooks/useMe'
import { v4 as uuidv4 } from 'uuid'
import { SIMPLE_EXTRA_FIELDS } from './confirmShipmentDetailsWidget.constants'
import type { ConfirmShipmentDetailsWidgetData } from 'types/widget.types'
import { ConfirmShipmentDetailsWidgetReadOnly } from './components/ConfirmShipmentDetailsWidgetReadOnly'
import { ConfirmShipmentDetailsWidgetEditable } from './components/ConfirmShipmentDetailsWidgetEditable'
import { typedArrayIncludes } from 'utils/type.utils'
import { isOrderLTL } from 'utils/order.utils'
import type {
    ArrayExtraFieldId,
    ConfirmShipmentDetailsCommodityErrors,
    ConfirmShipmentDetailsWidgetErrors,
    SimpleExtraFieldId,
} from './confirmShipmentDetailsWidget.types'
import {
    getDefaultCommodity,
    getDefaultStableArrayFieldIds,
    getDefaultVisibleSimpleFields,
} from './confirmShipmentDetailsWidget.utils'

export type ConfirmShipmentDetailsWidgetProps = WidgetComponentProps<
    'ConfirmShipmentDetails',
    ConfirmShipmentDetailsWidgetData
>

export const ConfirmShipmentDetailsWidget = ({
    messageId,
    widgetData,
    onSendMessage,
}: ConfirmShipmentDetailsWidgetProps) => {
    const [mutation, setMutation] = useState(widgetData)
    const [errors, setErrors] = useState<ConfirmShipmentDetailsWidgetErrors>({})
    const [stableArrayFieldIds, setStableArrayFieldIds] = useState(() =>
        getDefaultStableArrayFieldIds(widgetData)
    )
    const [visibleSimpleFieldsMap, setVisibleSimpleFieldsMap] = useState(() =>
        getDefaultVisibleSimpleFields(widgetData)
    )

    const isLTL = isOrderLTL(mutation)

    useEffect(() => {
        if (isLTL && !mutation.commodities?.length) {
            _onAddArrayField({
                id: 'commodities',
                object: getDefaultCommodity(widgetData),
            })
        }
        if (!isLTL) {
            setErrors((prev) => {
                return { ...prev, commodities: [] }
            })
        }
    }, [isLTL])

    const { me } = useMe({ refetchOnMount: false })

    const _onValidate = (data: Partial<typeof mutation>) => {
        const errors: ConfirmShipmentDetailsWidgetErrors = {}

        if (
            !data.pickupLocation?.placeId &&
            !data.pickupLocation?.addressBookId
        ) {
            errors.pickupLocation = 'Pickup location is required'
        }

        if (!data.pickupDateTimeStartAt) {
            errors.pickupDateTimeStartAt = 'Pickup date is required'
        }

        if (
            !data.dropoffLocation?.placeId &&
            !data.dropoffLocation?.addressBookId
        ) {
            errors.dropoffLocation = 'Delivery location is required'
        }

        // Pickup cannot be after delivery
        if (
            data.pickupDateTimeStartAt &&
            data.dropoffDateTimeStartAt &&
            new Date(data.pickupDateTimeStartAt) >
                new Date(data.dropoffDateTimeStartAt)
        ) {
            errors.pickupDateTimeStartAt =
                'Pickup date cannot be after delivery date'
        }

        if (data.numberOfShipments && data.numberOfShipments > 20) {
            errors.numberOfShipments = 'Max 20 loads allowed'
        }

        if (isLTL) {
            const commoditiesErrorsArr: ConfirmShipmentDetailsCommodityErrors[] =
                []

            data.commodities?.forEach((commodity) => {
                const commodityErrorsMap: ConfirmShipmentDetailsCommodityErrors =
                    {}

                if (!commodity.description) {
                    commodityErrorsMap.description = 'Description is required'
                }

                if (!commodity.weight) {
                    commodityErrorsMap.weight = 'Weight is required'
                }

                if (!commodity.quantity) {
                    commodityErrorsMap.quantity = 'Quantity is required'
                }

                if (!commodity.length) {
                    commodityErrorsMap.len = 'Length is required'
                }

                if (!commodity.width) {
                    commodityErrorsMap.width = 'Width is required'
                }

                if (!commodity.height) {
                    commodityErrorsMap.height = 'Height is required'
                }

                if (Object.keys(commodityErrorsMap).length) {
                    commoditiesErrorsArr.push(commodityErrorsMap)
                }
            })

            if (commoditiesErrorsArr.length) {
                errors.commodities = commoditiesErrorsArr
            }
        }

        if (!Object.keys(errors).length) {
            setErrors({})
            return
        }

        setErrors(errors)
        return errors
    }

    // Adds a new object to the corresponding array field
    // and generates a new unique ID for the front-end.
    const _onAddArrayField = (params: {
        id: ArrayExtraFieldId
        object: Record<string, unknown>
    }) => {
        setMutation((prev) => ({
            ...prev,
            [params.id]: [...(prev[params.id] ?? []), params.object],
        }))
        setStableArrayFieldIds((prev) => ({
            ...prev,
            [params.id]: [...prev[params.id], uuidv4()],
        }))
    }

    // Removes an object from the corresponding array field
    // based on the index and the field id, also removes
    // the stable ID we generated in _onAddArrayField.
    const _onRemoveArrayField = (params: {
        id: ArrayExtraFieldId
        index: number
    }) => {
        setMutation((prev) => ({
            ...prev,
            [params.id]: ((prev[params.id] ?? []) as unknown[]).filter(
                (_, index) => index !== params.index
            ),
        }))
        setStableArrayFieldIds((prev) => ({
            ...prev,
            [params.id]: prev[params.id].filter(
                (_, index) => index !== params.index
            ),
        }))

        if (params.id === 'commodities') {
            setErrors((prev) => {
                return {
                    ...prev,
                    commodities: prev.commodities?.filter(
                        (_, index) => index !== params.index
                    ),
                }
            })
        }
    }

    // Toggles the visibility of the simple extra fields.
    const _onAddSimpleField = (fieldId: SimpleExtraFieldId) => {
        setVisibleSimpleFieldsMap((prev) => ({
            ...prev,
            [fieldId]: true,
        }))

        if (['isApprovalRequired', 'isRushedOrder'].includes(fieldId)) {
            setMutation((prev) => ({
                ...prev,
                [fieldId]: true,
            }))
        }
    }

    // Removes the visibility of the simple extra fields and resets the values.
    const _onRemoveSimpleField = (fieldId: SimpleExtraFieldId) => {
        setVisibleSimpleFieldsMap((prev) => ({
            ...prev,
            [fieldId]: false,
        }))
        setMutation((prev) => ({
            ...prev,
            [fieldId]: undefined,
        }))
    }

    const _onAddFieldOptionClick = (
        fieldId: SimpleExtraFieldId | ArrayExtraFieldId
    ) => {
        if (typedArrayIncludes(SIMPLE_EXTRA_FIELDS, fieldId)) {
            _onAddSimpleField(fieldId)
        } else {
            if (fieldId === 'commodities') {
                _onAddArrayField({
                    id: fieldId,
                    object: getDefaultCommodity(widgetData),
                })
            } else {
                _onAddArrayField({ id: fieldId, object: {} })
            }
        }
    }

    const _onBook = () => {
        const errors = _onValidate(mutation)
        if (errors) {
            showToastError('Please fill in all required fields')
            return
        }

        // Clean up commodities with empty values.
        const cleanedCommodities = mutation.commodities?.filter(
            (commodity) =>
                !(
                    !commodity.description &&
                    !commodity.quantity &&
                    !commodity.freightType &&
                    !commodity.width &&
                    !commodity.height &&
                    !commodity.weight &&
                    !commodity.length &&
                    !commodity.numOfUnits
                )
        )

        onSendMessage({
            message: '[Action: Book]',
            payload: {
                replyToWidget: WIDGET_KEY.confirmShipmentDetails,
                replyToMessageId: messageId,
                widgetData: {
                    status: 'confirmed',
                    ...mutation,
                    commodities: cleanedCommodities,
                },
            },
        })
    }

    const _onCancel = () => {
        onSendMessage({
            message: '[Action: Cancel]',
            payload: {
                replyToWidget: WIDGET_KEY.confirmShipmentDetails,
                replyToMessageId: messageId,
                widgetData: { status: 'cancelled', ...mutation },
            },
        })
    }

    return widgetData.status ? (
        <ConfirmShipmentDetailsWidgetReadOnly data={widgetData} />
    ) : (
        <ConfirmShipmentDetailsWidgetEditable
            isLTL={isLTL}
            visibleSimpleFieldsMap={visibleSimpleFieldsMap}
            arrayFieldsIdsMap={stableArrayFieldIds}
            mutation={mutation}
            me={me}
            errors={errors}
            onChange={setMutation}
            onBook={_onBook}
            onCancel={_onCancel}
            onAddArrayField={_onAddArrayField}
            onRemoveSimpleField={_onRemoveSimpleField}
            onRemoveArrayField={_onRemoveArrayField}
            onAddFieldOptionClick={_onAddFieldOptionClick}
        />
    )
}
